/**
 * Basic math functions
 *
 * @module twofloat/math/basic
 */

import { NaN2, type TwoF64 } from '../base/common.js';
import { isNaN2 } from '../base/compare.js';
import { normalize } from '../base/eft.js';

/**
 * Return the absolute value of `x`.
 *
 * @param x A `f64` or `TwoF64` number
 * @returns The {@link TwoF64|`TwoF64`} representation of `|x|`
 */
export function abs(x: TwoF64): TwoF64;
export function abs([xhi, xlo]: TwoF64): TwoF64 {
  const hi = Math.abs(xhi);
  const lo = hi > xhi ? (xlo === 0 ? 0 : -xlo) : xlo;
  return [hi, lo];
}

/**
 * Return the negative of `x`.
 *
 * @param x A `TwoF64` number
 * @returns The {@link TwoF64|`TwoF64`} representation of `-x`
 */
export function neg(x: TwoF64): TwoF64;
export function neg([xhi, xlo]: TwoF64): TwoF64 {
  return [-xhi, xlo === 0 ? 0 : -xlo];
}

/**
 * Return the sign of `x`, that is, `[<sign>, 0]` where `<sign>` is either `-1`,
 * `-0`, `0` or `1` (or return `NaN2` if `x` is not a number).
 *
 * @param x A `TwoF64` number
 * @returns The {@link TwoF64|`TwoF64`} representation of the sign of `x`
 */
export function sign(x: TwoF64): TwoF64 {
  const s = Math.sign(x[0]);
  return Number.isFinite(s) ? [s, 0] : NaN2;
}

/**
 * Return the integral part of `x`, removing any fractional digits.
 *
 * @param x A `TwoF64` number
 * @returns The {@link TwoF64|`TwoF64`} representation of the integer part of `x`
 */
export function trunc(x: TwoF64): TwoF64;
export function trunc([xhi, xlo]: TwoF64): TwoF64 {
  if (!Number.isFinite(xhi)) {
    return Math.abs(xhi) !== Infinity ? NaN2 : [xhi, 0];
  }

  if (Number.isInteger(xhi)) {
    if (Number.isInteger(xlo)) {
      return [xhi, xlo];
    }

    const s = Math.sign(xhi);
    const [hi, lo] = normalize(xhi, s > 0 ? Math.floor(xlo) : Math.ceil(xlo));
    return hi === 0 ? [s*0, 0] : [hi, lo];
  }

  return [Math.trunc(xhi), 0];
}

/**
 * Return `⌊x⌋`, the greatest integer less than or equal to `x`.
 *
 * @param x A `TwoF64` number
 * @returns The {@link TwoF64|`TwoF64`} representation of `⌊x⌋`
 */
export function floor(x: TwoF64): TwoF64;
export function floor([xhi, xlo]: TwoF64): TwoF64 {
  if (!Number.isFinite(xhi)) {
    return Math.abs(xhi) !== Infinity ? NaN2 : [xhi, 0];
  }

  if (Number.isInteger(xhi)) {
    return Number.isInteger(xlo) ? [xhi, xlo] : normalize(xhi, Math.floor(xlo));
  }

  return [Math.floor(xhi), 0];
}

/**
 * Return `⌈x⌉`, the least integer greater than or equal to `x`.
 *
 * @param x A `TwoF64` number
 * @returns The {@link TwoF64|`TwoF64`} representation of `⌈x⌉`
 */
export function ceil(x: TwoF64): TwoF64;
export function ceil([xhi, xlo]: TwoF64): TwoF64 {
  if (!Number.isFinite(xhi)) {
    return Math.abs(xhi) !== Infinity ? NaN2 : [xhi, 0];
  }

  if (Number.isInteger(xhi)) {
    if (Number.isInteger(xlo)) {
      return [xhi, xlo];
    }

    const [hi, lo] = normalize(xhi, Math.ceil(xlo));
    return hi === 0 ? [Math.sign(xhi)*0, 0] : [hi, lo];
  }

  return [Math.ceil(xhi), 0];
}

/**
 * Return `⌊x⌉`, the nearest integer to `x`, with ties (fractional values of
 * `0.5`) being rounded toward positive infinity.
 *
 * @param x A `TwoF64` number
 * @returns The {@link TwoF64|`TwoF64`} representation of `⌊x⌉`
 */
export function round(x: TwoF64): TwoF64;
export function round([xhi, xlo]: TwoF64): TwoF64 {
  if (!Number.isFinite(xhi)) {
    return Math.abs(xhi) !== Infinity ? NaN2 : [xhi, 0];
  }

  if (Number.isInteger(xhi)) {
    return Number.isInteger(xlo) ? [xhi, xlo] : normalize(xhi, Math.round(xlo));
  }

  if (Math.abs(xhi) === 0.5 && xlo !== 0) {
    const s = Math.sign(xhi);
    // eslint-disable-next-line unicorn/prefer-minimal-ternary
    return s === Math.sign(xlo) ? [s, 0] : [s*0, 0];
  }

  return [Math.round(xhi), 0];
}

/**
 * Return the maximum of the given arguments (at least one must be provided).
 *
 * @param arg1 A `TwoF64` number
 * @param args More `TwoF64` numbers
 * @returns The maximum {@link TwoF64|`TwoF64`} number
 */
export function max(arg1: TwoF64, ...args: TwoF64[]): TwoF64;
/**
 * Return the maximum of the given arguments (at least one must be provided).
 *
 * @param args One or more `TwoF64` numbers
 * @returns The maximum {@link TwoF64|`TwoF64`} number
 */
export function max(...args: TwoF64[]): TwoF64;
export function max([hi, lo]: TwoF64, ...args: TwoF64[]): TwoF64 {
  if (isNaN2([hi, lo])) {
    return NaN2;
  }

  for (const [xhi, xlo] of args) {
    if (hi < xhi || (hi === xhi && lo < xlo)) {
      [hi, lo] = [xhi, xlo];
    }
    else if (isNaN2([xhi, xlo])) {
      return NaN2;
    }
  }

  // eslint-disable-next-line unicorn/prefer-minimal-ternary
  return Math.abs(hi) === Infinity ? [hi, 0] : [hi, lo];
}

/**
 * Return the minimum of the given arguments (at least one must be provided).
 *
 * @param arg1 A `TwoF64` number
 * @param args More `TwoF64` numbers
 * @returns The minimum {@link TwoF64|`TwoF64`} number
 */
export function min(arg1: TwoF64, ...args: TwoF64[]): TwoF64;
export function min(...args: TwoF64[]): TwoF64;
export function min([hi, lo]: TwoF64, ...args: TwoF64[]): TwoF64 {
  if (isNaN2([hi, lo])) {
    return NaN2;
  }

  for (const [xhi, xlo] of args) {
    if (hi > xhi || (hi === xhi && lo > xlo)) {
      [hi, lo] = [xhi, xlo];
    }
    else if (isNaN2([xhi, xlo])) {
      return NaN2;
    }
  }

  // eslint-disable-next-line unicorn/prefer-minimal-ternary
  return Math.abs(hi) === Infinity ? [hi, 0] : [hi, lo];
}
